import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../api/client";
import ConfirmModal from "../../components/ConfirmModal";
import { Field } from "./CartoDAEAdmin";

const ETATS = [
  { v: "degagee",      l: "Dégagée",      c: "#27ae60" },
  { v: "en_cours",     l: "En cours",     c: "#f39c12" },
  { v: "non_traitee",  l: "Non traitée",  c: "#e74c3c" },
  { v: "impraticable", l: "Impraticable", c: "#2c3e50" },
  { v: "inondee",      l: "Inondée",      c: "#2980b9" },
  { v: "inconnue",     l: "Inconnue",     c: "#bdc3c7" },
];

const labelEtat = (e) => (ETATS.find((x) => x.v === e) || { l: e }).l;
const colorEtat = (e) => (ETATS.find((x) => x.v === e) || { c: "#bdc3c7" }).c;

// Extrait la liste de points [lng,lat] d'un geojson LineString ou MultiLineString
function geojsonToPoints(g) {
  if (!g) return [];
  if (g.type === "LineString") return g.coordinates || [];
  if (g.type === "MultiLineString") {
    // On concatène les segments à plat (pour redessin)
    return (g.coordinates || []).flat();
  }
  return [];
}

export default function CartoVoiriesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importer, setImporter] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [editor, setEditor] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/cartographie/voiries")).items || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setEtat = async (id, etat) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/cartographie/voiries/${id}/etat`,
                       { etat, commentaire: "" });
      load();
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setBusyId(null); }
  };

  const confirmDelete = async () => {
    try { await api.del(`/admin/cartographie/voiries/${toDelete.id}`);
          setToDelete(null); load();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
        <button onClick={() => setImporter(true)}>🌐 Importer depuis OpenStreetMap</button>
        <button className="secondary" onClick={() => setDrawer(true)}>
          ✏️ Tracer manuellement
        </button>
      </div>
      <p className="muted" style={{ fontSize: "0.85em", marginTop: "0.4rem" }}>
        L'import OSM propose les routes nommées d'une commune. Vous cochez celles
        à suivre, en un clic.
      </p>

      {loading && <p className="muted mt-2">Chargement…</p>}
      {!loading && items.length === 0 &&
        <p className="muted mt-2">Aucune voirie enregistrée.</p>}

      {items.length > 0 && (
        <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {items.map((v) => (
            <VoirieRow key={v.id} voirie={v}
                       busy={busyId === v.id}
                       onSetEtat={(e) => setEtat(v.id, e)}
                       onEdit={() => setEditor(v)}
                       onDelete={() => setToDelete(v)} />
          ))}
        </div>
      )}

      {importer && (
        <OSMImporter onClose={() => setImporter(false)}
                     onSaved={() => { setImporter(false); load(); }} />
      )}
      {drawer && (
        <ManualEditor mode="create"
                      onClose={() => setDrawer(false)}
                      onSaved={() => { setDrawer(false); load(); }} />
      )}
      {editor && (
        <ManualEditor mode="edit" voirie={editor}
                      onClose={() => setEditor(null)}
                      onSaved={() => { setEditor(null); load(); }} />
      )}
      {toDelete && (
        <ConfirmModal title="Supprimer cette voirie ?"
                      message={`« ${toDelete.nom} » sera définitivement supprimée.`}
                      confirmLabel="Supprimer" variant="danger"
                      onConfirm={confirmDelete} onCancel={() => setToDelete(null)} />
      )}
    </div>
  );
}

function VoirieRow({ voirie, busy, onSetEtat, onEdit, onDelete }) {
  const color = colorEtat(voirie.etat);
  const maj = voirie.etat_maj_at
    ? new Date(voirie.etat_maj_at).toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : null;
  return (
    <div className="card" style={{ padding: "0.7rem 0.9rem", borderLeft: `5px solid ${color}` }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 600 }}>{voirie.nom}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6,
                        fontSize: "0.85em", marginTop: 2 }}>
            <span style={{ width: 10, height: 10, background: color,
                           borderRadius: "50%" }} />
            <span>{labelEtat(voirie.etat)}</span>
            {maj && <span className="muted">· maj {maj}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button className="small" onClick={onEdit}>Modifier</button>
          <button className="small danger" onClick={onDelete}>Supprimer</button>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
        {ETATS.filter((e) => e.v !== "inconnue").map((e) => (
          <button key={e.v} className="small"
                  disabled={busy || voirie.etat === e.v}
                  onClick={() => onSetEtat(e.v)}
                  style={{
                    background: voirie.etat === e.v ? e.c : "#fff",
                    color: voirie.etat === e.v ? "#fff" : e.c,
                    borderColor: e.c, border: `1.5px solid ${e.c}`,
                    fontWeight: 500,
                  }}>
            {e.l}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================ OSM IMPORTER ============================

function CommuneSearch({ value, onChoose }) {
  const [q, setQ] = useState(value?.nom || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (q === (value?.nom || "")) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/cartographie/communes-search?q=${encodeURIComponent(q)}`);
        setResults(r.items || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [q, value]);

  return (
    <div style={{ position: "relative" }}>
      <input type="text" value={q}
             onChange={(e) => setQ(e.target.value)}
             onFocus={() => setOpen(true)}
             onBlur={() => setTimeout(() => setOpen(false), 200)}
             placeholder="Tapez le nom de la commune…" />
      {open && results.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0,
                      background: "#fff", border: "1px solid rgba(0,0,0,0.15)",
                      borderRadius: 4, maxHeight: 220, overflowY: "auto",
                      zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          {results.map((c) => (
            <div key={c.code}
                 onMouseDown={(e) => e.preventDefault()}
                 onClick={() => { onChoose(c); setQ(c.nom); setOpen(false); }}
                 style={{ padding: "0.45rem 0.7rem", cursor: "pointer",
                          borderBottom: "1px solid rgba(0,0,0,0.05)" }}
                 onMouseEnter={(e) => e.target.style.background = "#f4f7fb"}
                 onMouseLeave={(e) => e.target.style.background = ""}>
              <div style={{ fontWeight: 500 }}>{c.nom}</div>
              <div className="muted" style={{ fontSize: "0.78em" }}>
                {c.codeDepartement} · INSEE {c.code}
                {c.population ? ` · ${c.population.toLocaleString("fr-FR")} hab.` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OSMImporter({ onClose, onSaved }) {
  const [commune, setCommune] = useState(null); // null = commune par défaut config
  const [phase, setPhase] = useState("idle"); // idle | loading | choice | submitting | done
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const fetchSuggest = async () => {
    setError(""); setPhase("loading");
    try {
      const url = commune
        ? `/admin/cartographie/voiries/osm-suggest?insee=${commune.code}`
        : "/admin/cartographie/voiries/osm-suggest";
      const r = await api.get(url);
      setItems(r.items || []);
      const sel = {};
      (r.items || []).forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      setPhase("choice");
    } catch (e) {
      setError("Échec interrogation OSM : " + (e.message || ""));
      setPhase("idle");
    }
  };

  const submit = async () => {
    setError(""); setPhase("submitting");
    try {
      const voiries = items.filter((_, i) => selected[i]);
      const r = await api.post("/admin/cartographie/voiries/osm-import",
                                { voiries });
      setResult(r); setPhase("done");
    } catch (e) {
      setError(e.message || "Erreur");
      setPhase("choice");
    }
  };

  const allChecked = items.length > 0 && items.every((_, i) => selected[i]);
  const someChecked = items.some((_, i) => selected[i]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 640, width: "95vw" }}>
        <div className="modal-header">
          <h3>Importer des voiries depuis OpenStreetMap</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Commune (vide = commune par défaut du site)</label>
            <CommuneSearch value={commune} onChoose={setCommune} />
            {commune && (
              <div className="muted" style={{ fontSize: "0.78em", marginTop: 4 }}>
                ✓ {commune.nom} · INSEE {commune.code}
                <button className="small" style={{ marginLeft: 8 }}
                        onClick={() => setCommune(null)}>Réinitialiser</button>
              </div>
            )}
          </div>

          {phase === "idle" && (
            <div className="mt-2">
              <button onClick={fetchSuggest}>
                🔍 Récupérer les voiries
                {commune ? ` de ${commune.nom}` : " de la commune par défaut"}
              </button>
            </div>
          )}
          {phase === "loading" && (
            <p className="muted mt-2">Interrogation d'Overpass (peut prendre ~10s)…</p>
          )}
          {phase === "choice" && items.length === 0 && (
            <p className="muted mt-2">Aucune voirie trouvée.</p>
          )}
          {phase === "choice" && items.length > 0 && (
            <>
              <p className="muted mt-2" style={{ fontSize: "0.85em" }}>
                {items.length} voirie{items.length > 1 ? "s" : ""} trouvée{items.length > 1 ? "s" : ""}.
                Décochez celles à exclure.
              </p>
              <div style={{ marginBottom: "0.5rem" }}>
                <label style={{ cursor: "pointer", display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <input type="checkbox" style={{ width: "auto" }}
                         checked={allChecked}
                         onChange={(e) => {
                           const sel = {};
                           items.forEach((_, i) => { sel[i] = e.target.checked; });
                           setSelected(sel);
                         }} />
                  <span>{allChecked ? "Tout décocher" : "Tout cocher"}</span>
                </label>
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto",
                            border: "1px solid rgba(0,0,0,0.1)", borderRadius: 4,
                            padding: "0.4rem" }}>
                {items.map((it, i) => (
                  <label key={i} style={{ display: "flex", gap: 8,
                                          padding: "0.3rem", cursor: "pointer",
                                          borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                    <input type="checkbox" style={{ width: "auto" }}
                           checked={!!selected[i]}
                           onChange={(e) =>
                             setSelected({ ...selected, [i]: e.target.checked })} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{it.nom}</div>
                      <div className="muted" style={{ fontSize: "0.78em" }}>
                        {it.nb_segments} segment{it.nb_segments > 1 ? "s" : ""} · {it.nb_points} points
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
          {phase === "submitting" && <p className="muted mt-2">Import en cours…</p>}
          {phase === "done" && result && (
            <div className="success mt-2">
              {result.imported} voirie{result.imported > 1 ? "s" : ""} importée{result.imported > 1 ? "s" : ""}.
              {result.skipped > 0 && (
                <div className="muted mt-1">
                  ({result.skipped} ignorée{result.skipped > 1 ? "s" : ""} : doublon)
                </div>
              )}
            </div>
          )}
          {error && <div className="error mt-2">{error}</div>}
        </div>
        <div className="modal-footer">
          {phase === "done" ? (
            <button onClick={onSaved}>OK</button>
          ) : (
            <>
              <button className="secondary" onClick={onClose}>Annuler</button>
              {phase === "choice" && (
                <button onClick={submit} disabled={!someChecked}>
                  Importer la sélection
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================ MANUAL EDITOR (create + edit) ============================

function ManualEditor({ mode, voirie, onClose, onSaved }) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const pointsRef = useRef([]);
  const polylineRef = useRef(null);
  const [, forceUpdate] = useState(0);
  const [nom, setNom] = useState(voirie?.nom || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const redraw = () => {
    const L = window.L;
    if (polylineRef.current) { polylineRef.current.remove(); polylineRef.current = null; }
    if (pointsRef.current.length >= 2) {
      const latlngs = pointsRef.current.map(([lng, lat]) => [lat, lng]);
      polylineRef.current = L.polyline(latlngs,
        { color: "#1d4f91", weight: 5 }).addTo(leafletMap.current);
    }
    forceUpdate((x) => x + 1);
  };

  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current || leafletMap.current) return;

    // Position initiale : centre du tracé existant si edit, sinon mairie
    let center = [48.1102, 0.0815];
    let zoom = 16;
    if (mode === "edit" && voirie?.geojson) {
      const pts = geojsonToPoints(voirie.geojson);
      pointsRef.current = pts.map(([lng, lat]) => [lng, lat]);
      if (pts.length > 0) {
        const lats = pts.map((p) => p[1]);
        const lngs = pts.map((p) => p[0]);
        center = [
          (Math.min(...lats) + Math.max(...lats)) / 2,
          (Math.min(...lngs) + Math.max(...lngs)) / 2,
        ];
      }
    }

    leafletMap.current = L.map(mapRef.current).setView(center, zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap", maxZoom: 19,
    }).addTo(leafletMap.current);

    // Dessine le tracé existant si edit
    if (pointsRef.current.length >= 2) {
      const latlngs = pointsRef.current.map(([lng, lat]) => [lat, lng]);
      polylineRef.current = L.polyline(latlngs,
        { color: "#1d4f91", weight: 5 }).addTo(leafletMap.current);
      const bounds = polylineRef.current.getBounds();
      if (bounds.isValid()) leafletMap.current.fitBounds(bounds, { padding: [20, 20] });
    }

    leafletMap.current.on("click", (e) => {
      pointsRef.current.push([e.latlng.lng, e.latlng.lat]);
      redraw();
    });

    setTimeout(() => leafletMap.current && leafletMap.current.invalidateSize(), 100);
    return () => {
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
    // eslint-disable-next-line
  }, []);

  const undo = () => {
    pointsRef.current.pop();
    redraw();
  };

  const reset = () => {
    pointsRef.current = [];
    redraw();
  };

  const submit = async () => {
    setError("");
    if (!nom.trim()) { setError("Indiquez un nom."); return; }
    if (pointsRef.current.length < 2) {
      setError("Au moins 2 points requis sur la carte."); return;
    }
    setBusy(true);
    try {
      const geojson = { type: "LineString", coordinates: pointsRef.current };
      const payload = { nom: nom.trim(), commentaire: voirie?.commentaire || "", geojson };
      if (mode === "edit" && voirie?.id) {
        await api.put(`/admin/cartographie/voiries/${voirie.id}`, payload);
      } else {
        await api.post("/admin/cartographie/voiries", payload);
      }
      onSaved();
    } catch (e) { setError(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 920, width: "95vw" }}>
        <div className="modal-header">
          <h3>{mode === "edit" ? "Modifier la voirie" : "Tracer une voirie manuellement"}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="muted" style={{ fontSize: "0.85em" }}>
            {mode === "edit"
              ? "Tracé existant chargé. Cliquez pour ajouter des points, ou tout effacer pour redessiner."
              : "Cliquez sur la carte pour ajouter des points le long de la voirie."}
          </p>
          <div ref={mapRef} className="carto-editor-map" />
          <div style={{ display: "flex", gap: 6, marginTop: "0.5rem", flexWrap: "wrap" }}>
            <button className="small" onClick={undo}
                    disabled={pointsRef.current.length === 0}>
              ↩ Annuler dernier point
            </button>
            <button className="small secondary" onClick={reset}
                    disabled={pointsRef.current.length === 0}>
              ✕ Tout effacer
            </button>
            <span className="muted" style={{ fontSize: "0.85em",
                                             alignSelf: "center", marginLeft: 6 }}>
              {pointsRef.current.length} point{pointsRef.current.length > 1 ? "s" : ""}
            </span>
          </div>
          <Field label="Nom de la voirie" value={nom} onChange={setNom}
                 placeholder="Ex : Route de Conlie" />
          {error && <div className="error mt-2">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="secondary" onClick={onClose}>Annuler</button>
          <button onClick={submit} disabled={busy || pointsRef.current.length < 2}>
            {busy ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
